import { CollectionConfig } from "@rebasepro/types";
import jobsCollection from "./jobs";

/**
 * What went to the LinkedIn page, what it said, and what did not go.
 *
 * The log behind `crons/post-jobs-to-linkedin.ts`. It matters more than most
 * logs here because the thing it records cannot be re-read from our own
 * systems: a post is on LinkedIn, written by a model, and the only copy of the
 * words is this row.
 *
 * ── The one screen this is genuinely for ─────────────────────────────
 *
 * `LINKEDIN_AUTOPOST` starts false, and while it is false this table fills with
 * `drafted` rows that were never published. That is the point: somebody reads a
 * week of what the cron *would* have said before any of it is public, and the
 * decision to switch it on is made against real drafts rather than a sample.
 * Which is why `commentary` is a full-width text field here and not a truncated
 * column — it is meant to be read.
 *
 * Table, constraints and RLS in drizzle/0059. Admin-only. Out of the drawer:
 * nobody *works* here, they look.
 */
const linkedinPostsCollection: CollectionConfig = {
    name: "Publicaciones en LinkedIn",
    singularName: "Publicación en LinkedIn",
    slug: "linkedin-posts",
    table: "linkedin_posts",
    securityRules: [
        {
            // Admin only, and no second rule. An employer has no business
            // reading the queue, and a candidate sees the finished post on
            // LinkedIn like everybody else.
            name: "admin_full_access",
            operation: "all",
            roles: ["admin"]
        }
    ],
    properties: {
        id: {
            name: "ID",
            type: "string",
            isId: "uuid"
        },
        job_id: {
            name: "Vacante",
            type: "relation",
            relation: {
                kind: "belongsTo",
                target: () => jobsCollection,
                localKey: "job_id"
            },
            description: "La vacante que se anuncia. Una fila por vacante: volver a publicarla exige borrar esta a mano.",
            validation: { required: true },
            admin: { readOnly: true }
        },
        status: {
            name: "Estado",
            type: "string",
            enum: [
                { id: "drafted", label: "Redactada, sin publicar", color: "gray" },
                { id: "posted", label: "Publicada", color: "green" },
                { id: "failed", label: "Error al publicar", color: "red" },
                { id: "rejected", label: "Rechazada en revisión", color: "orange" }
            ],
            // The labels say what happened, not what the enum is called: only
            // "Publicada" means anything is public, and the other three have to
            // be distinguishable at a glance from a list.
            description: "«Redactada» no ha salido de aquí. «Rechazada» no se reintenta sola: la redacción no pasó la revisión.",
            admin: { readOnly: true }
        },
        commentary: {
            name: "Texto de la publicación",
            type: "string",
            description: "Lo que dice el post, tal y como se envió. No se puede regenerar: el modelo no escribe dos veces el mismo párrafo.",
            admin: { readOnly: true, multiline: true }
        },
        headline: {
            name: "Título de la tarjeta",
            type: "string",
            description: "El titular de la vista previa del enlace. Se construye con columnas, no lo escribe el modelo.",
            admin: { readOnly: true }
        },
        description: {
            name: "Subtítulo de la tarjeta",
            type: "string",
            description: "Dónde, qué contrato, qué nivel y qué salario, si la oferta lo dice.",
            admin: { readOnly: true }
        },
        llm_model: {
            name: "Modelo",
            type: "string",
            description: "Quién escribió el texto. La misma procedencia que guarda jobs.llm_model.",
            admin: { readOnly: true }
        },
        warnings: {
            name: "Avisos de la revisión",
            type: "array",
            columnType: "text[]",
            of: { type: "string", name: "Aviso" },
            defaultValue: [],
            description: "Lo que la revisión recortó o marcó sin llegar a rechazarlo. Vacío es lo normal.",
            admin: { readOnly: true }
        },
        post_url: {
            name: "Ver en LinkedIn",
            type: "string",
            url: true,
            description: "La publicación. Es el único sitio donde se puede editar o borrar.",
            admin: { readOnly: true }
        },
        post_urn: {
            name: "URN",
            type: "string",
            description: "urn:li:share:… — lo que devuelve LinkedIn en la cabecera x-restli-id.",
            admin: { readOnly: true }
        },
        image_urn: {
            name: "URN de la imagen",
            type: "string",
            description: "La tarjeta subida. Sin ella el post sale sin imagen: LinkedIn no lee la página enlazada.",
            admin: { readOnly: true }
        },
        attempts: {
            name: "Intentos",
            type: "number",
            defaultValue: 0,
            description: "Intentos de publicación. Un valor > 0 en estado «Redactada» significa que no sabemos si llegó a salir.",
            admin: { readOnly: true }
        },
        last_error: {
            name: "Último error",
            type: "string",
            description: "Lo que respondió LinkedIn, o las razones por las que la revisión rechazó el borrador.",
            admin: { readOnly: true, multiline: true }
        },
        drafted_at: {
            name: "Redactada",
            type: "date",
            admin: { readOnly: true }
        },
        posted_at: {
            name: "Publicada",
            type: "date",
            description: "Sólo se escribe cuando LinkedIn ha confirmado la publicación y la ha identificado.",
            admin: { readOnly: true }
        },
        created_at: {
            name: "Creada",
            type: "date",
            autoValue: "on_create"
        },
        updated_at: {
            name: "Actualizada",
            type: "date",
            autoValue: "on_update"
        }
    },
    admin: {
        group: "Vacantes",
        icon: "Share",
        // Out of the drawer, like `digest_sends`: a log of machine-written rows
        // is not a place anybody does work. Reachable by URL and from the
        // vacancy it belongs to.
        hideFromNavigation: true,
        sort: ["created_at", "desc"],
        listProperties: ["job_id", "status", "posted_at"],
        display: {
            title: "headline",
            subtitle: "status",
            date: "posted_at"
        },
        previewProperties: ["job_id", "status", "posted_at"]
    }
};

export default linkedinPostsCollection;
